import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  effect,
  AfterViewInit,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { LocaleService } from '../../../../../core/services/locale.service';

/**
 * Tab id literal — kept narrow so misspellings fail the type checker
 * rather than silently routing to a non-existent tab.
 */
export type BmpTabId = 'meeting' | 'rooms' | 'people' | 'notes' | 'options';

export interface BmpTabCounts {
  meeting: number;
  rooms: number;
  people: number;
  notes: number;
  options: number;
}

/**
 * Tabs where the form has a missing required field — drives the soft
 * pulse so the user knows where to look when the CTA is disabled.
 * Optional; absent / empty record = no pulse anywhere.
 */
export type BmpTabValidation = Partial<Record<BmpTabId, boolean>>;

interface TabDef {
  id: BmpTabId;
  label: string;
}

const TAB_IDS: BmpTabId[] = ['meeting', 'rooms', 'people', 'notes', 'options'];

/**
 * BmpTabBarComponent — sticky tab bar for the Book Meeting form.
 *
 * Mirrors the visual pattern of `space-detail-panel`'s `.dp-tab-bar`
 * (the polished panel in the app) so the booking surface matches the
 * detail surface. Differences from `dp-tab-bar`:
 *
 *   - Click-driven indicator (no scroll-tied tracking) — the form
 *     doesn't have an intersecting-section model; each tab is its
 *     own pane.
 *   - Validation pulse: the parent panel passes `validation` per tab;
 *     a tab with a missing required field gets a soft warning ring so
 *     the user knows where to look when the CTA is disabled.
 *
 * Pure presentational atom. The parent owns `activeTab`, the counts,
 * and the validation state. The bar emits `tabChanged` and that's it.
 */
@Component({
  selector: 'bmp-tab-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="bmp-tab-bar" role="tablist" [attr.aria-label]="tablistAriaLabel()">
      <div class="bmp-tab-bar__tabs" #tabsEl>
        @for (tab of tabs(); track tab.id) {
          <button
            type="button"
            class="bmp-tab"
            role="tab"
            #tabBtn
            [id]="'bmp-tab-' + tab.id"
            [class.is-active]="activeTab() === tab.id"
            [class.has-warning]="!!validation()[tab.id]"
            [attr.aria-selected]="activeTab() === tab.id"
            [attr.aria-controls]="'bmp-sec-' + tab.id"
            [attr.tabindex]="activeTab() === tab.id ? 0 : -1"
            [attr.data-tab]="tab.id"
            [attr.data-testid]="'bmp-tab-' + tab.id"
            (click)="onTabClick(tab.id)">
            <span class="bmp-tab__label">{{ tab.label }}</span>
            @let n = countFor(tab.id);
            @if (n > 0) {
              <span class="bmp-tab__count" [attr.aria-label]="n + ' ' + tab.label">
                {{ n }}
              </span>
            }
            @if (validation()[tab.id]) {
              <span class="bmp-tab__warn-dot" aria-hidden="true"></span>
            }
          </button>
        }
        <span class="bmp-tab-bar__indicator"
              [style.left.px]="indicatorLeft()"
              [style.width.px]="indicatorWidth()"></span>
      </div>
    </div>
  `,
  styles: `
    /* Sticky to the top of the scroll container — pinned via top:0 so
       form content scrolls *under* the bar, never beside it. The host
       background matches the panel canvas (--surface-base, the warm
       cream on workplace-paper) so the bar visually merges with the
       paper rather than reading as a separate white strip. The panel
       SCSS bleeds the bar out to the full panel edge and cancels the
       scroll container's top padding when the bar is the first child. */
    :host {
      display: block;
      flex: none;
      position: sticky;
      top: 0;
      z-index: 8;
      background: var(--surface-base, var(--surface-raised, var(--card-bg-solid)));
    }

    .bmp-tab-bar {
      display: flex;
      align-items: center;
      padding: 0 14px;
      border-bottom: 1px solid var(--divider);
    }

    /* The tabs strip itself — position:relative so the sliding
       indicator can position absolutely within. */
    .bmp-tab-bar__tabs {
      position: relative;
      display: flex;
      align-items: stretch;
      gap: 2px;
      overflow-x: auto;
      scrollbar-width: none;
      flex: 1;
      &::-webkit-scrollbar { display: none; }
    }

    .bmp-tab {
      position: relative;
      padding: 10px 14px 8px;
      font-size: 12px;
      font-weight: 500;
      font-family: inherit;
      line-height: 1;
      color: var(--text-tertiary);
      background: transparent;
      border: none;
      border-radius: 0;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      white-space: nowrap;
      transition:
        color var(--motion-fast, 120ms) var(--ease, ease),
        font-weight var(--motion-fast, 120ms) var(--ease, ease);

      &:hover { color: var(--text-secondary); }

      &.is-active {
        color: var(--text);
        font-weight: 600;
      }

      &:focus-visible {
        outline: 2px solid var(--brand-focus-ring, var(--color-primary));
        outline-offset: -2px;
      }
    }

    .bmp-tab__count {
      font-size: 10px;
      font-weight: 700;
      background: var(--hover-overlay);
      border-radius: 999px;
      min-width: 16px;
      height: 16px;
      padding: 0 5px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: var(--text-secondary);

      .bmp-tab.is-active & {
        background: var(--pressed-overlay);
        color: var(--text);
      }
    }

    /* Warning dot — appears on tabs whose required fields are unfilled.
       Soft pulse + colour shift draws the eye without crying wolf. */
    .bmp-tab__warn-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--warning-strong);
      box-shadow: 0 0 0 0 var(--warning-strong);
      animation: bmp-tab-pulse 1.6s ease-out infinite;
    }

    .bmp-tab.has-warning {
      color: var(--warning-strong);
      &.is-active { color: var(--warning-strong); }
    }

    @keyframes bmp-tab-pulse {
      0%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--warning-strong) 55%, transparent); }
      70%  { box-shadow: 0 0 0 6px transparent; }
      100% { box-shadow: 0 0 0 0 transparent; }
    }

    /* Sliding indicator — 2px line under the active tab. Position is
       driven per-frame by the parent panel's scroll handler (via the
       progress input) so no CSS transition is needed; adding one
       would fight the scroll-driven updates and make the motion feel
       sluggish. Click-to-tab still works because the panel suppresses
       scroll-spy for ~600ms during the in-flight smooth scroll. */
    .bmp-tab-bar__indicator {
      position: absolute;
      bottom: 0;
      height: 2px;
      background: var(--text);
      border-radius: 1px;
      pointer-events: none;
    }

    /* The panel is mounted for the whole session and slides in from the right, so while it is
       closed (.bmp without .show) these dots sit off-screen. An infinite box-shadow animation
       there still restyles and repaints every frame (a box-shadow cannot run on the compositor):
       three dots on every page, measured as a running animation on Home, Schedule and Map. */
    :host-context(.bmp:not(.show)) .bmp-tab__warn-dot { animation: none; }

    @media (prefers-reduced-motion: reduce) {
      .bmp-tab { transition: none; }
      .bmp-tab__warn-dot { animation: none; }
    }
  `,
})
export class BmpTabBarComponent implements AfterViewInit {
  readonly activeTab = input.required<BmpTabId>();
  readonly counts = input<BmpTabCounts>({
    meeting: 0,
    rooms: 0,
    people: 0,
    notes: 0,
    options: 0,
  });
  readonly validation = input<BmpTabValidation>({});

  /**
   * Fractional progress (0…1) from the active tab toward the NEXT tab.
   * Drives the indicator to slide smoothly as the user scrolls between
   * sections rather than jumping when `activeTab` flips. 0 = indicator
   * sits flush under the active tab; 1 = it has fully arrived at the
   * next tab's position. Smoothstep eased internally for natural feel.
   * Pass `0` (the default) to disable scroll tracking entirely — the
   * indicator then just measures the active tab's geometry on click.
   */
  readonly progress = input<number>(0);

  readonly tabChanged = output<BmpTabId>();

  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected readonly tabs = computed<TabDef[]>(() => {
    this.locale.locale();
    return TAB_IDS.map(id => ({
      id,
      label: this.transloco.translate(`viewer.book_meeting_panel.tab_bar.${id}`),
    }));
  });

  protected readonly tablistAriaLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.tab_bar.tablist_aria');
  });

  private readonly tabsEl = viewChild<ElementRef<HTMLElement>>('tabsEl');

  // Indicator geometry — recomputed whenever the active tab changes or
  // the view is laid out. Defaults to 0/0 so the first frame is invisible
  // rather than a flash at the wrong position.
  protected readonly indicatorLeft = signal(0);
  protected readonly indicatorWidth = signal(0);

  constructor() {
    // Recompute indicator position on every active-tab change OR
    // fractional-progress change. Uses queueMicrotask so the layout
    // has settled before we measure tab geometry.
    effect(() => {
      this.activeTab();
      this.counts(); // counts can change tab widths
      this.progress(); // scroll-driven fractional tracking
      queueMicrotask(() => this.updateIndicator());
    });
  }

  ngAfterViewInit(): void {
    this.updateIndicator();
  }

  protected countFor(id: BmpTabId): number {
    return this.counts()[id] ?? 0;
  }

  protected onTabClick(id: BmpTabId): void {
    if (id === this.activeTab()) return;
    this.tabChanged.emit(id);
  }

  private updateIndicator(): void {
    const host = this.tabsEl()?.nativeElement;
    if (!host) return;
    const active = host.querySelector<HTMLElement>(
      `.bmp-tab[data-tab="${this.activeTab()}"]`,
    );
    if (!active) return;

    const progress = Math.max(0, Math.min(1, this.progress()));
    if (progress <= 0) {
      // No scroll tracking — indicator sits flush under the active tab.
      this.indicatorLeft.set(active.offsetLeft);
      this.indicatorWidth.set(active.offsetWidth);
      return;
    }

    // Scroll-driven mode — interpolate between the active tab and the
    // NEXT tab in DOM order so the indicator slides ahead as the user
    // scrolls toward the next section. Smoothstep easing gives the
    // motion a natural feel — pure linear felt mechanical.
    const next = active.nextElementSibling as HTMLElement | null;
    if (!next || !next.classList.contains('bmp-tab')) {
      // No next tab (last tab) — just sit on the active one.
      this.indicatorLeft.set(active.offsetLeft);
      this.indicatorWidth.set(active.offsetWidth);
      return;
    }

    const eased = progress * progress * (3 - 2 * progress); // smoothstep
    const left = active.offsetLeft + (next.offsetLeft - active.offsetLeft) * eased;
    const width = active.offsetWidth + (next.offsetWidth - active.offsetWidth) * eased;
    this.indicatorLeft.set(left);
    this.indicatorWidth.set(width);
  }
}
